import type { ApiErrorResponse } from "@shared/types";

function getCsrfToken(): string | undefined {
	const match = document.cookie.match(/(?:^|;\s*)csrf-token=([^;]*)/);
	return match ? decodeURIComponent(match[1]) : undefined;
}

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
		public errors?: Record<string, string[]> | string,
		public code?: number,
	) {
		super(message);
		this.name = "ApiError";
	}

	get isValidationError(): boolean {
		return this.status === 422 && typeof this.errors === "object" && this.errors !== null;
	}

	get fieldErrors(): Record<string, string[]> {
		if (typeof this.errors === "object" && this.errors !== null && !Array.isArray(this.errors)) {
			return this.errors as Record<string, string[]>;
		}
		return {};
	}
}

interface RequestOptions<TBody = unknown> {
	method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	body?: TBody;
	headers?: Record<string, string>;
	signal?: AbortSignal;
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export async function apiClient<TResponse>(
	path: string,
	options?: RequestOptions,
): Promise<TResponse> {
	const method = options?.method ?? "GET";

	const headers: Record<string, string> = {
		accept: "application/json",
		...options?.headers,
	};

	// CSRF-Token für mutating requests
	if (MUTATING_METHODS.has(method)) {
		const csrfToken = getCsrfToken();
		if (csrfToken) {
			headers["x-csrf-token"] = csrfToken;
		}
	}

	let body: BodyInit | undefined;

	if (options?.body !== undefined) {
		if (options.body instanceof FormData) {
			// Content-Type NICHT setzen -- Browser setzt Boundary automatisch
			body = options.body;
		} else {
			headers["content-type"] = "application/json";
			body = JSON.stringify(options.body);
		}
	}

	const response = await fetch(path, {
		method,
		headers,
		body,
		credentials: "same-origin",
		signal: options?.signal,
	});

	if (!response.ok) {
		let errorData: ApiErrorResponse | undefined;
		try {
			errorData = (await response.json()) as ApiErrorResponse;
		} catch {
			// Response ist kein JSON -- generische Fehlermeldung verwenden
		}

		// Laravel antwortet bei Feldfehlern mit einem Array statt einem String --
		// etwa $validator->errors()->get('image') in ApiV3CustomerImageController.
		// Ohne diesen Zweig fiel jede solche Antwort auf den generischen Text
		// zurueck, auch wenn das Backend genau benannt hat, was falsch war.
		const rawMessage = errorData?.message;
		const message =
			typeof rawMessage === "string"
				? rawMessage
				: Array.isArray(rawMessage) && typeof rawMessage[0] === "string"
					? rawMessage[0]
					: response.statusText || "Ein Fehler ist aufgetreten";

		throw new ApiError(response.status, message, errorData?.errors, errorData?.code);
	}

	// 204 No Content
	if (response.status === 204) {
		return undefined as TResponse;
	}

	// Immer als JSON parsen versuchen -- manche Backend-Responses haben falsche Content-Type Header
	try {
		return (await response.json()) as TResponse;
	} catch {
		return undefined as TResponse;
	}
}

export const api = {
	get: <T>(path: string, signal?: AbortSignal) => apiClient<T>(path, { method: "GET", signal }),
	post: <T, B = unknown>(path: string, body?: B) => apiClient<T>(path, { method: "POST", body }),
	put: <T, B = unknown>(path: string, body?: B) => apiClient<T>(path, { method: "PUT", body }),
	patch: <T, B = unknown>(path: string, body?: B) => apiClient<T>(path, { method: "PATCH", body }),
	delete: <T, B = unknown>(path: string, body?: B) =>
		apiClient<T>(path, { method: "DELETE", body }),
	upload: <T>(path: string, formData: FormData) =>
		apiClient<T>(path, { method: "PUT", body: formData }),
	postUpload: <T>(path: string, formData: FormData) =>
		apiClient<T>(path, { method: "POST", body: formData }),
} as const;
